import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrumb from "../components/BreadCrumb";
import { FaSpinner } from "react-icons/fa";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { fetchGitHubCategories, fetchCategoryMedia, getGitHubCdnUrl, testGitHubApi } from "../utils/githubMediaUtils";

const CategoryGallery = () => {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchData = async () => {
      setLoading(true);
      try {
        // Test GitHub API
        await testGitHubApi();
        
        if (categoryId) {
          // Fetch all categories to get the current one's details
          const categories = await fetchGitHubCategories();
          const currentCategory = categories.find(c => c.id === categoryId);
          
          if (currentCategory) {
            setCategory(currentCategory);
            // Fetch media for this category
            const categoryMedia = await fetchCategoryMedia(categoryId);
            console.log("Fetched media:", categoryMedia); // Debug log
            setMedia(categoryMedia);
          } else {
            setError(`Category with ID ${categoryId} not found`);
          }
        } else {
          setError("No category ID provided");
        }
      } catch (err) {
        console.error("Error fetching category data:", err);
        setError("Failed to load category data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [categoryId]);

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  const paths = [
    { label: t("homepage"), link: "/" },
    { label: t("portfolio"), link: "/portfolio" },
    ...(category ? [{ label: category.title, link: `/category-gallery/${category.id}` }] : []),
  ];

  // Prepare slides for lightbox
  const getLightboxSlides = () => {
    return media.map(item => {
      if (item.mediaType === 'video') {
        return {
          type: 'video',
          width: 1280,
          height: 720,
          sources: [
            {
              src: item.bannerImage,
              type: `video/${item.bannerImage.split('.').pop().toLowerCase()}`
            }
          ]
        };
      }
      return { 
        src: item.rawUrl || item.bannerImage 
      };
    });
  };

  // Function to handle image error
  const handleImageError = (e, item) => {
    console.log("Image failed to load:", e.target.src);
    
    // If using jsDelivr URL, try direct GitHub raw URL
    if (e.target.src === item.bannerImage && item.rawUrl) {
      console.log("Trying raw URL instead:", item.rawUrl);
      e.target.src = item.rawUrl;
    } else {
      // If both fail, use placeholder
      e.target.src = "/images/placeholder.jpg";
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-white min-h-screen flex items-center justify-center pt-32">
        <FaSpinner className="animate-spin text-primary text-4xl" />
      </div>
    );
  }

  return (
    <div className="w-full bg-white min-h-screen">
      <div className="pt-32">
        <Breadcrumb paths={paths} />
      </div>
      <div className="container mx-auto px-3 lg:px-0 pb-32">
        <div className="pt-10 pb-10">
          <div className="flex flex-col leading-relaxed tracking-normal">
            <h1 className="font-northuraBlack text-4xl md:text-6xl lg:text-7xl">
              {category ? category.title : t("portfolio")}
            </h1>
            <p className="text-3xl md:text-5xl lg:text-6xl font-northuraBold">
              {t("gallery")}
            </p>
          </div>
        </div>

        {error && (
          <div className="py-6 text-center">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Media Grid */}
        {media.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {media.map((item, idx) => (
              <div 
                key={idx} 
                onClick={() => openLightbox(idx)} 
                className="cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-full h-60 relative bg-black rounded-lg overflow-hidden group">
                  <img
                    src={item.bannerImage}
                    alt={item.title}
                    className="w-full h-full object-cover rounded-lg transition-opacity duration-500 ease-in-out opacity-80 group-hover:opacity-100"
                    onError={(e) => handleImageError(e, item)}
                    loading="lazy"
                  />
                  {item.mediaType === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-primary/80 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-3 p-2">
                  <h3 className="text-lg font-semibold line-clamp-2">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        ) : !loading && !error && (
          <div className="py-20 text-center">
            <p className="text-gray-500">No media found in this category.</p>
          </div>
        )}
      </div>

      {/* Lightbox for viewing media */}
      <Lightbox
        open={isOpen}
        close={() => setIsOpen(false)}
        index={photoIndex}
        slides={getLightboxSlides()}
        plugins={[Fullscreen, Slideshow, Thumbnails, Video, Zoom]}
      />
    </div>
  );
};

export default CategoryGallery; 